// Apple's appTransactionId: one stable id per Apple Account in the App Store
// build (spec §16). Field and parameter names live here only, so a server
// rename is a one-line change.
export const APPLE_ACCOUNT = {
  // Heartbeat and App Store transaction rows.
  recordField: 'app_transaction_id',
  // GET client/heart_beat_of_device
  heartbeatLookupParam: 'app_transaction_id',
  // GET admin/appstore/transactions
  transactionsLookupParam: 'app_transaction_id',
  // GET admin/blocked_payer
  blockedLookupParam: 'appTransactionIds',
  // POST admin/block_user_paypal_payer, when heartbeat ids could not be read
  partialFlag: 'apple_ids_partial',
  partialReason: 'apple_ids_partial_reason',
} as const

export function appTransactionIdOf(rec: any): string | null {
  const value = rec?.[APPLE_ACCOUNT.recordField]
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

// 封关联支付账户 bans the Apple accounts the server could read; this says when
// some could not be.
export function appleIdsPartial(result: any): { reason: string | null } | null {
  if (result?.[APPLE_ACCOUNT.partialFlag] !== true) return null
  return { reason: result[APPLE_ACCOUNT.partialReason] ?? null }
}

export function shortAppTransactionId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id
}

// admin/blocked_payer answers each id with its ban row, several rows, or null.
export function isAppleAccountBlocked(blockedById: Record<string, unknown> | null | undefined, id: string): boolean {
  return Boolean(blockedById?.[id])
}

export interface AppleAccountRefund {
  transactionId: string
  productId: string
  revokedAt: string | null
}

export interface RelatedAppleUser {
  userId: number
  sources: Array<'heartbeat' | 'transaction'>
  lastSeen: string | null
  refunds: AppleAccountRefund[]
}

// A malformed date blanks that one time instead of failing the whole lookup.
const isoOrNull = (raw: unknown): string | null => {
  if (!raw) return null
  const time = new Date(raw as string)
  return Number.isNaN(time.getTime()) ? null : time.toISOString()
}

const heartbeatTime = (rec: any): string | null =>
  isoOrNull(rec?.lastSeen?.iso || rec?.lastSeen || rec?.updatedAt || rec?.createdAt)

const later = (a: string | null, b: string | null) => (!a ? b : !b ? a : a > b ? a : b)

// Every user seen with one Apple Account: from heartbeats (about two months)
// and App Store purchases (kept forever), newest first, with their refunds.
export function relatedAppleAccountUsers({ heartbeats, transactions }: { heartbeats: any[], transactions: any[] }): RelatedAppleUser[] {
  const byUser = new Map<number, RelatedAppleUser>()
  const entry = (userId: number) => {
    let user = byUser.get(userId)
    if (!user) {
      user = { userId, sources: [], lastSeen: null, refunds: [] }
      byUser.set(userId, user)
    }
    return user
  }
  const addSource = (user: RelatedAppleUser, source: RelatedAppleUser['sources'][number]) => {
    if (!user.sources.includes(source)) user.sources.push(source)
  }
  for (const rec of heartbeats) {
    const userId = Number(rec?.userId)
    if (!Number.isInteger(userId) || userId <= 0) continue
    const user = entry(userId)
    addSource(user, 'heartbeat')
    user.lastSeen = later(user.lastSeen, heartbeatTime(rec))
  }
  for (const tx of transactions) {
    const userId = Number(tx?.user_id)
    if (!Number.isInteger(userId) || userId <= 0) continue
    const user = entry(userId)
    addSource(user, 'transaction')
    user.lastSeen = later(user.lastSeen, isoOrNull(tx.purchase_date))
    if (tx.revocation_date) {
      user.refunds.push({
        transactionId: tx.transaction_id,
        productId: tx.product_id,
        revokedAt: isoOrNull(tx.revocation_date),
      })
    }
  }
  return [...byUser.values()].sort((a, b) => (b.lastSeen || '').localeCompare(a.lastSeen || ''))
}

// Lookups for many cards at once (the refund requests page): each id is
// fetched once and only one request runs at a time. A failed lookup is
// dropped from the cache so it can be retried.
export function createSerialLookup<T>(fetch: (id: string) => Promise<T>): (id: string) => Promise<T> {
  const cache = new Map<string, Promise<T>>()
  let queue: Promise<unknown> = Promise.resolve()
  return (id: string) => {
    const cached = cache.get(id)
    if (cached) return cached
    const result = queue.then(() => fetch(id))
    queue = result.catch(() => {})
    cache.set(id, result)
    result.catch(() => cache.delete(id))
    return result
  }
}
